<template>
  <section
    :class="[
      'ucrs-propertyRow',
      {
        'ucrs-propertyRow__noBorder': noBorder,
      },
    ]"
  >
    <article class="ucrs-propertyRow__inner">
      <div
        :class="[
          'hidden lg:flex absolute h-full z-20 overflow-hidden',
          'transform transition duration-500',
          {
            'lg:w-168 xl:w-192': size === 'large',
            'translate-x-0': mapShow,
            '-translate-x-full': !mapShow,
          },
        ]"
        @mouseover="mapToggleShow = true"
        @mouseout="mapToggleShow = false"
      >
        <LazyGoogleMaps
          v-if="mapRender"
          class="w-full h-full"
          no-gesture
          no-events
          no-poi
          zoom
          :zoom-config="{
            minZoom: 15,
            maxZoom: 19,
            position: 'TOP_LEFT',
          }"
          :items="mapItem"
        />
        <div
          :class="[
            'absolute right-0 h-full w-12 flex items-center justify-center bg-blue cursor-pointer',
            'transform transition-transform duration-500',
            {
              'translate-x-0': mapToggleShow,
              'translate-x-full': !mapToggleShow,
            },
          ]"
          @click="mapShow = !mapShow"
        >
          <SvgIcon name="angle-left-white" class="w-7 h-7" />
        </div>
      </div>
      <div
        :class="[
          'ucrs-propertyRow__image',
          `ucrs-propertyRow__image--${size}`,
          'transform transition duration-500',
          {
            'scale-90': mapShow,
          },
        ]"
      >
        <PropertyImgLoader
          v-if="property.images && property.images.length"
          absolute
        />
        <ClientOnly>
          <LazySwiper
            v-if="property.images && property.images.length"
            :options="swiperOptions"
            navigation
            pagination
            overlay
            virtual
            nav-position="bottom-center"
            @slide-index="currentSlide = $event + 1"
          >
            <div
              v-for="(img, index) in property.images"
              :key="index"
              class="swiper-slide aspect-video"
              style="width: 480px"
            >
              <NuxtImg
                :src="$utilities.imgAlias(img.src)"
                :alt="img.alt"
                :loading="index === 0 ? null : 'lazy'"
                :preload="index === 0"
                width="480"
                height="270"
                format="webp"
                @click="openDetail(href)"
              />
            </div>
          </LazySwiper>
          <template #placeholder>
            <PropertyRowSwiperSkeleton />
          </template>
        </ClientOnly>
        <div class="ucrs-propertyRow__carouselGeo">
          <SvgIcon :name="computedIconName" class="w-7 h-6 mr-2" />
          <p class="ucrs-propertyRow__carouselGeo-inner">
            <strong>{{ property.location.city }}</strong>
            <span
              v-if="property.location.locality.neighbourhood"
              :title="property.location.locality.neighbourhood"
            >
              <span class="inline-block mx-2">|</span>
              {{ property.location.locality.neighbourhood }}
            </span>
          </p>
        </div>
        <div
          v-if="property.images && property.images.length"
          class="ucrs-propertyRow__carouselCounter"
        >
          <span v-text="currentSlide"></span>/
          <span v-text="property.images.length"></span>
        </div>
        <div class="ucrs-propertyRow__carouselIcons">
          <SvgIcon
            v-if="property.features.virtual_tour"
            name="virtual-tour-new"
            class="w-17 h-17"
          />
          <SvgIcon
            v-if="property.features.url_video"
            name="video-new"
            class="w-17 h-17"
          />
        </div>
        <div v-if="partner" class="ucrs-propertyRow__carouselPartner">
          <NuxtImg
            :src="partner.img"
            :alt="`Logo ${partner.name}`"
            :width="partner.width"
            :height="partner.height"
          />
        </div>
      </div>
      <div class="ucrs-propertyRow__body">
        <NuxtLink
          class="absolute top-0 bottom-0 right-0 left-0 z-10 md:hidden"
          :to="href"
          target="_blank"
        />
        <div class="ucrs-propertyRow__body-top">
          <p :class="['mb-1.6 xl:mb-3', { 'line-clamp-1': size === 'medium' }]">
            <strong
              class="hidden md:inline-block mr-2"
              v-text="property.location.city"
            ></strong>
            <span
              v-if="property.location.locality.neighbourhood"
              v-text="property.location.locality.neighbourhood"
            />
          </p>
          <h3 class="ucrs-propertyRow__title">
            <NuxtLink
              v-dompurify-html="property.title"
              :to="href"
              target="_blank"
            />
          </h3>
        </div>
        <div class="ucrs-propertyRow__body-center">
          <div v-if="size !== 'medium'">
            <p
              v-if="property.location.locality.thoroughfare.address"
              class="ucrs-propertyRow__address"
              @click="onAddressClick"
            >
              <SvgIcon name="marker-blue" class="w-7 h-7 mr-4" />
              <span
                v-dompurify-html="
                  property.location.locality.thoroughfare.address
                "
              ></span>
            </p>
            <ul class="ucrs-propertyRow__features">
              <li
                v-for="(feat, i) in features"
                :key="i"
                v-dompurify-html="feat"
                class="ucrs-propertyRow__feature"
              ></li>
            </ul>
          </div>
          <p
            :class="[
              'ucrs-propertyRow__price',
              {
                'ucrs-propertyRow__price--reserved':
                  property.transaction.price_upon_request,
              },
            ]"
            :title="computedPropertyPrice"
          >
            {{ computedPropertyPrice }}
          </p>
        </div>
        <div class="ucrs-propertyRow__body-bottom">
          <ul
            v-if="showAgent && property.agent && property.agent.contact"
            class="ucrs-propertyRow__agent"
          >
            <li
              v-if="
                property.agent.contact.first_name ||
                property.agent.contact.last_name
              "
              class="ucrs-propertyRow__agentItem"
            >
              <SvgIcon name="person" class="ucrs-propertyRow__agentIcon" />
              <span class="ucrs-propertyRow__agentText">
                Consulente:
                {{
                  `${property.agent.contact.first_name} ${property.agent.contact.last_name}`
                }}
              </span>
            </li>
            <li
              v-if="property.agent.contact.email"
              class="ucrs-propertyRow__agentItem"
            >
              <SvgIcon
                name="envelope-new"
                class="ucrs-propertyRow__agentIcon"
              />
              <a
                :href="`mailto:${property.agent.contact.email}`"
                class="ucrs-propertyRow__agentText hover:underline"
                >{{ property.agent.contact.email }}</a
              >
            </li>
            <li
              v-if="property.agent.contact.phone"
              class="ucrs-propertyRow__agentItem"
            >
              <SvgIcon name="phone" class="ucrs-propertyRow__agentIcon" />
              <a
                :href="agentPhoneLink"
                class="ucrs-propertyRow__agentText hover:underline"
                >{{ property.agent.contact.phone }}</a
              >
            </li>
            <!-- @FIXME: property.consultant.mandate_expiry. -->
            <!-- <li v-if="property.consultant.mandate_expiry" class="ucrs-propertyRow__agentItem">
                <SvgIcon name="calendar" class="ucrs-propertyRow__agentIcon" />
                <span class="ucrs-propertyRow__agentText"
                  >Scadenza mandato: {{ computedExpires }}</span
                >
              </li> -->
          </ul>
          <p
            v-else
            :class="[
              'ucrs-propertyRow__description',
              `ucrs-propertyRow__description--${size}`,
            ]"
          >
            {{ property.description }}
          </p>
        </div>
        <div v-if="href" class="flex justify-end lg:ml-10">
          <LazyPropertyActions
            class="hidden md:flex"
            :property="property"
            :show-notify="false"
            @favorite-add="$set(property, 'is_favorite', true)"
            @favorite-remove="$set(property, 'is_favorite', false)"
            @hidden-add="$set(property, 'is_hidden', true)"
            @hidden-remove="$set(property, 'is_hidden', false)"
          />
          <div class="hidden md:block">
            <UcrsButton
              class="md:ml-4"
              type="primary"
              @click="openDetail(href)"
            >
              Vai al dettaglio
            </UcrsButton>
          </div>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import { defineAsyncComponent } from 'vue'

// Static assets
import { propertyPrice } from '~/assets/js/methods'
import { features, partner } from '~/assets/js/computed'
import { iconName } from '~/assets/js/property'

// Components
import UcrsButton from '~/components/UcrsButton'
import PropertyImgLoader from '~/components/PropertyImgLoader'
import PropertyRowSwiperSkeleton from '~/components/PropertyRowSwiperSkeleton'

const LazyGoogleMaps = defineAsyncComponent({
  loader: () => import('~/components/GoogleMaps'),
})
const LazyPropertyActions = defineAsyncComponent({
  loader: () => import('~/components/PropertyActions'),
})
const LazySwiper = defineAsyncComponent({
  loader: () => import('~/components/Swiper'),
  loadingComponent: PropertyRowSwiperSkeleton,
  delay: 0,
})

export default {
  components: {
    UcrsButton,
    PropertyImgLoader,
    PropertyRowSwiperSkeleton,
    LazyGoogleMaps,
    LazyPropertyActions,
    LazySwiper,
  },

  props: {
    property: {
      type: Object,
      default: () => {
        return null
      },
    },
    size: {
      type: String,
      default: 'medium',
    },
    href: {
      type: String,
      default: '',
    },
    showAgent: {
      type: Boolean,
      default: false,
    },

    noBorder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentSlide: 1,
      swiperOptions: {
        observer: true,
        observeParents: true,
        watchOverflow: true,
        noSwipingClass: 'swiper-slide',
      },
      mapRender: false,
      mapShow: false,
      mapToggleShow: false,
    }
  },
  computed: {
    features,
    partner,

    computedIconName() {
      const category = this.property.building.category.name
      const luxury = this.property.building.is_luxury
      const agent = this.property.agent?.type

      return iconName(category, luxury, agent)
    },

    computedPropertyPrice() {
      const { property } = this

      if (property.private_negotiation) {
        return 'Trattativa riservata'
      }

      return this.propertyPrice(property)
    },

    // computedExpires() {
    //   const options = {
    //     day: '2-digit',
    //     month: '2-digit',
    //     year: '2-digit',
    //   }
    //   return new Date(this.property.expires).toLocaleString('it-IT', options)
    // },

    agentPhoneLink() {
      if (!this.property.agent?.contact?.phone) {
        return
      }

      const phone = this.property.agent.contact.phone.trim().replace(/\s+/g, '')
      return `tel:+39${phone}`
    },

    mapItem() {
      const { property } = this

      if (!property) return

      const { id } = property
      const { latitude, longitude } = property.location.locality

      return [{ id, latitude, longitude }]
    },
  },

  watch: {
    mapShow(mapShow) {
      if (mapShow) {
        this.$tealium.track({
          event_name: 'annuncio_mappa_open',
          id: this.property.id,
        })

        return
      }

      this.$tealium.track({
        event_name: 'annuncio_mappa_close',
        id: this.property.id,
      })
    },
  },

  methods: {
    propertyPrice,

    updateCurrentSlide(activeIndex) {
      this.currentSlide = activeIndex + 1
    },

    onAddressClick() {
      if (!this.mapRender) {
        this.mapRender = true
        this.mapShow = true
        return
      }

      this.mapShow = !this.mapShow
    },

    openDetail(url) {
      const route = this.$router.resolve(url)
      window.open(route.href)
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/button';
@import '~/assets/css/components/property-row';
</style>
